import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useSelector } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SlidersHorizontal, Signpost, Move, Plus, Trash2, Grid2x2, Users, IdCard, Loader2, Check } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { RulesetsApi, RulesetInput, Ruleset } from '@/services/rules/rulesetsApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { LayoutsApi, Layout } from '@/services/sites/layoutsApi'
import { SearchApi } from '@/services/core/searchApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { StatsBar, StatsBarId, StatsBarActivity } from '../../components/StatsBar'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ChipMultiSelect } from '../../components/ChipMultiSelect'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ResourceUsageCard } from '@/components/ResourceUsageCard'
import type { ResourceUsage } from '@/services/core/listSearch'

const TARGETING_TYPE_OPTIONS = [
  'DAY_PARTING',
  'USER_SEGMENT',
  'RETARGETING',
  'DEVICE_PROPERTY',
  'NAMED_LOCATION',
  'CATEGORY',
  'DOMAIN_NAME',
  'AD_UNIT_MATCHING_LABEL',
  'KEYWORD',
  'THIRD_PARTY_AUDIENCE',
  'WEATHER',
  'SEMANTIC_CATEGORY',
  'GEOSPATIAL',
  'AD_UNIT',
  'IP_ADDRESS',
  'PUBLISHER',
  'ARTICLE',
  'SITE',
  'SITE_GROUP',
  'SIMILAR_KEYWORD',
  'KEY_VALUE',
  'AD_UNIT_VIEWABILITY',
  'SITE_COUNTRY',
  'DATE',
].map((v) => ({ label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), value: v }))

const CURRENCY_OPTIONS = [
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
  { label: 'GBP', value: 'GBP' },
  { label: 'NOK', value: 'NOK' },
  { label: 'SEK', value: 'SEK' },
  { label: 'DKK', value: 'DKK' },
]

interface TeamFloorPriceEntry {
  teamId: string
  teamName: string
  amount: number
  currency: string
}

interface AdvertiserFloorPriceEntry {
  advertiserId: string
  advertiserName: string
  amount: number
  currency: string
}

interface SizeEntry {
  width: number | null
  height: number | null
}

// ── Inner form component ────────────────────────────────────────────────────

const NO_USAGE: ResourceUsage = { publishers: [], sites: [], adUnits: [] }

interface RulesetEditFormProps {
  ruleset: Ruleset | null
  /** What the ruleset applies to when loaded; saving writes the difference. */
  usage: ResourceUsage
  id: string
  isNew: boolean
}

function RulesetEditForm({ ruleset, usage, id, isNew }: RulesetEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()
  const { data: allTeams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  // Keep the ruleset's own teams, named by the ruleset response, even when the list lacks them
  const teams = [
    ...(ruleset?.teams ?? []).filter((rt) => !allTeams.some((t) => t.id === rt.id)),
    ...allTeams.filter((t) => t.type !== 'AX_ADVERTISER'),
  ]

  const TYPE_OPTIONS = [
    { label: t('rulesets.typeInventory'), value: 'INVENTORY' },
    { label: t('rulesets.typeTargeting'), value: 'TARGETING' },
    { label: t('rulesets.typeSize'), value: 'SIZE' },
    { label: t('rulesets.typeLayout'), value: 'LAYOUT' },
  ]

  // Load advertiser teams (AX_ADVERTISER type) — only when dropdown is opened
  const [advertiserTeamsEnabled, setAdvertiserTeamsEnabled] = useState(false)
  const { data: advertiserTeams = [] } = useQuery({
    queryKey: ['advertiserTeams'],
    queryFn: TeamsApi.getAdvertiserTeams,
    enabled: advertiserTeamsEnabled,
    staleTime: Infinity,
  })

  // Load advertisers via search
  const { data: advertisers = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['advertisersSearch'],
    queryFn: async () => {
      const res = await SearchApi.search({
        q: '',
        objectState: 'ACTIVE',
        orderBy: 'NAME',
        pageSize: 50,
        types: 'Advertiser',
      })
      return res.searchResults.map((r) => ({ id: r.id, name: r.name }))
    },
    enabled: hasToken,
    staleTime: Infinity,
  })

  const { data: layouts = [] } = useQuery<Layout[]>({
    queryKey: ['layouts'],
    queryFn: () => LayoutsApi.getAll(),
    enabled: hasToken,
    staleTime: Infinity,
  })

  const teamFPsFromRuleset: TeamFloorPriceEntry[] = ruleset
    ? ruleset.advertiserFloorPrices
        .filter((afp) => !!afp.team)
        .map((afp) => ({
          teamId: afp.team!.id,
          teamName: afp.team!.name,
          amount: afp.floorPrice.amount,
          currency: afp.floorPrice.currency,
        }))
    : []
  const advFPsFromRuleset: AdvertiserFloorPriceEntry[] = ruleset
    ? ruleset.advertiserFloorPrices
        .filter((afp) => !!afp.advertiser)
        .map((afp) => ({
          advertiserId: afp.advertiser!.id,
          advertiserName: afp.advertiser!.name,
          amount: afp.floorPrice.amount,
          currency: afp.floorPrice.currency,
        }))
    : []

  const form = useForm({
    defaultValues: {
      name: ruleset?.name ?? '',
      type: ruleset?.type ?? 'INVENTORY',
      floorPriceCurrency: ruleset?.floorPrice?.currency ?? 'EUR',
      floorPriceAmount: ruleset?.floorPrice?.amount ?? null as number | null,
      teamIds: ruleset?.teams.map((t) => t.id) ?? [] as string[],
      targetingTypes: ruleset?.targetingTypes ?? [] as string[],
      sizes: (ruleset?.sizes ?? []).map((s) => ({
        width: s.dimensions[0] ?? null,
        height: s.dimensions[1] ?? null,
      })) as SizeEntry[],
      layoutIds: (ruleset?.layouts ?? []).map((l) => l.id) as string[],
      teamFloorPrices: teamFPsFromRuleset,
      advertiserFloorPrices: advFPsFromRuleset,
      usage,
    },
    onSubmit: ({ value }) => {
      const advertiserFloorPricesPayload = [
        ...value.teamFloorPrices.map((t) => ({
          team: { id: t.teamId },
          floorPrice: { currency: t.currency, amount: t.amount },
        })),
        ...value.advertiserFloorPrices.map((a) => ({
          advertiser: { id: a.advertiserId },
          floorPrice: { currency: a.currency, amount: a.amount },
        })),
      ]
      const input: RulesetInput = {
        name: value.name,
        type: value.type,
        objectState: (ruleset?.objectState ?? 'ACTIVE') as ObjectState,
        floorPrice: value.floorPriceAmount != null
          ? { currency: value.floorPriceCurrency, amount: value.floorPriceAmount }
          : null,
        advertiserFloorPrices: advertiserFloorPricesPayload,
        targetingTypes: value.type === 'TARGETING' ? value.targetingTypes : undefined,
        sizes: value.type === 'SIZE'
          ? value.sizes
              .filter((s) => s.width != null && s.height != null)
              .map((s) => ({ dimensions: [s.width!, s.height!] }))
          : undefined,
        layouts: value.type === 'LAYOUT' ? value.layoutIds.map((id) => ({ id })) : undefined,
        teams: value.teamIds.map((id) => ({ id })),
      }
      mutation.mutate({ input, usage: value.usage })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => RulesetsApi.update(id!, { name: ruleset?.name ?? '', objectState }),
    ['ruleset', id],
    ['rulesets']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const addTeamFloorPrice = () => {
    const tfps = form.getFieldValue('teamFloorPrices')
    const currency = form.getFieldValue('floorPriceCurrency')
    form.setFieldValue('teamFloorPrices', [
      ...tfps,
      { teamId: '', teamName: '', amount: 0, currency },
    ])
  }

  const updateTeamFloorPrice = (index: number, field: keyof TeamFloorPriceEntry, value: string | number) => {
    const tfps = form.getFieldValue('teamFloorPrices')
    form.setFieldValue('teamFloorPrices', tfps.map((t, i) => (i === index ? { ...t, [field]: value } : t)))
  }

  const removeTeamFloorPrice = (index: number) => {
    const tfps = form.getFieldValue('teamFloorPrices')
    form.setFieldValue('teamFloorPrices', tfps.filter((_, i) => i !== index))
  }

  const handleTeamSelect = (index: number, teamId: string) => {
    const team = advertiserTeams.find((t) => t.id === teamId)
    const tfps = form.getFieldValue('teamFloorPrices')
    form.setFieldValue('teamFloorPrices', tfps.map((t, i) =>
      i === index ? { ...t, teamId, teamName: team?.name ?? '' } : t
    ))
  }

  const addAdvertiserFloorPrice = () => {
    const afps = form.getFieldValue('advertiserFloorPrices')
    const currency = form.getFieldValue('floorPriceCurrency')
    form.setFieldValue('advertiserFloorPrices', [
      ...afps,
      { advertiserId: '', advertiserName: '', amount: 0, currency },
    ])
  }

  const updateAdvertiserFloorPrice = (index: number, field: keyof AdvertiserFloorPriceEntry, value: string | number) => {
    const afps = form.getFieldValue('advertiserFloorPrices')
    form.setFieldValue('advertiserFloorPrices', afps.map((a, i) => (i === index ? { ...a, [field]: value } : a)))
  }

  const removeAdvertiserFloorPrice = (index: number) => {
    const afps = form.getFieldValue('advertiserFloorPrices')
    form.setFieldValue('advertiserFloorPrices', afps.filter((_, i) => i !== index))
  }

  const handleAdvertiserSelect = (index: number, advertiserId: string) => {
    const adv = advertisers.find((a) => a.id === advertiserId)
    const afps = form.getFieldValue('advertiserFloorPrices')
    form.setFieldValue('advertiserFloorPrices', afps.map((a, i) =>
      i === index ? { ...a, advertiserId, advertiserName: adv?.name ?? '' } : a
    ))
  }

  const addSize = () => {
    const s = form.getFieldValue('sizes')
    form.setFieldValue('sizes', [...s, { width: null, height: null }])
  }

  const updateSize = (index: number, field: keyof SizeEntry, value: number | null) => {
    const s = form.getFieldValue('sizes')
    form.setFieldValue('sizes', s.map((e, i) => (i === index ? { ...e, [field]: value } : e)))
  }

  const removeSize = (index: number) => {
    const s = form.getFieldValue('sizes')
    form.setFieldValue('sizes', s.filter((_, i) => i !== index))
  }

  const mutation = useMutation({
    mutationFn: async ({ input, usage: nextUsage }: { input: RulesetInput; usage: ResourceUsage }) => {
      const saved = isNew ? await RulesetsApi.create(id!, input) : await RulesetsApi.update(id!, input)
      // The ruleset's usage lives on the other objects, so each is saved there.
      if (!isNew) await RulesetsApi.setUsage(id, saved.type, usage, nextUsage)
      return saved
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['ruleset', id], data)
      queryClient.invalidateQueries({ queryKey: ['ruleset-usage', id] })
      queryClient.setQueryData(['rulesets'], (old: Ruleset[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((r) => r.id === data.id)
        return idx >= 0 ? old.map((r) => (r.id === data.id ? data : r)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/publishing/rulesets/${id}`, { replace: !createDialog })
      }
      toast.success(t('common.saved'), {
        description: isNew ? t('rulesets.createdSuccess') : t('rulesets.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError ? error.message : isNew ? t('rulesets.createFailed') : t('rulesets.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, {
        description: detail,
        duration: 5000,
      })
    },
  })

  const crumbs = [
    { label: t('nav.rulesets'), href: '/publishing/rulesets' },
    { label: isNew ? t('rulesets.newTitle') : (ruleset?.name ?? t('rulesets.editTitle')) },
  ]

  const teamFPs = useSelector(form.store, (s) => s.values.teamFloorPrices)
  const advFPs = useSelector(form.store, (s) => s.values.advertiserFloorPrices)
  const sizes = useSelector(form.store, (s) => s.values.sizes)
  const selectedType = useSelector(form.store, (s) => s.values.type)

  // Merge existing entries with fetched list so names show before dropdown is opened
  const teamDropdownOptions = (() => {
    const existing = teamFPs
      .filter((t) => t.teamId && t.teamName)
      .map((t) => ({ id: t.teamId, name: t.teamName }))
    const merged = new Map(existing.map((t) => [t.id, t]))
    advertiserTeams.forEach((t) => merged.set(t.id, t))
    return Array.from(merged.values())
  })()

  const advertiserDropdownOptions = (() => {
    const existing = advFPs
      .filter((a) => a.advertiserId && a.advertiserName)
      .map((a) => ({ id: a.advertiserId, name: a.advertiserName }))
    const merged = new Map(existing.map((a) => [a.id, a]))
    advertisers.forEach((a) => merged.set(a.id, a))
    return Array.from(merged.values())
  })()

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={ruleset?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.ruleset')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('rulesets.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {ruleset && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={ruleset!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {ruleset && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={ruleset.id} />,
              },
              {
                label: t('common.type'),
                content: (
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ad-text)' }}>
                    {ruleset.type}
                  </span>
                ),
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(ruleset.createTime)}
                    updated={formatDate(ruleset.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={SlidersHorizontal} />
                  {t('rulesets.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('common.type')}</label>
                    <form.Field name="type">
                      {(field) => (
                        <Select label={t('common.type')} value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')} disabled={!isNew}>
                          <SelectTrigger><SelectValue>{TYPE_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                          <SelectContent>
                            {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('rulesets.floorPrice')}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <form.Field name="floorPriceAmount">
                        {(field) => (
                          <NumberInput
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v ?? null)}
                            min={0}
                            placeholder={t('rulesets.amountPlaceholder')}
                            style={{ flex: 1 }}
                          />
                        )}
                      </form.Field>
                      <form.Field name="floorPriceCurrency">
                        {(field) => (
                          <Select value={field.state.value} onValueChange={(v) => field.handleChange(v ?? '')}>
                            <SelectTrigger style={{ width: 100 }}><SelectValue>{CURRENCY_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                            <SelectContent>
                              {CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                  </div>
                  <div className="p-field full">
                    <label>{t('common.teams')}</label>
                    <form.Field name="teamIds">
                      {(field) => (
                        <ChipMultiSelect
                          value={field.state.value}
                          onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                          options={teams}
                          optionLabel="name"
                          optionValue="id"
                          placeholder={t('rulesets.selectTeams')}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedType === 'TARGETING' && (
              <Card tone="delivery">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Signpost} />
                    {t('rulesets.targetingTypes')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('rulesets.targetingTypes')}</label>
                      <form.Field name="targetingTypes">
                        {(field) => (
                          <ChipMultiSelect
                            value={field.state.value}
                            onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                            options={TARGETING_TYPE_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            placeholder={t('rulesets.selectTargetingTypes')}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedType === 'SIZE' && (
              <Card tone="creative">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Move} />
                    {t('rulesets.sizes')}
                    {sizes.length > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--ad-text-muted)',
                          background: 'var(--ad-neutral-soft)',
                          padding: '1px 8px',
                          borderRadius: 999,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {sizes.length}
                      </span>
                    )}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addSize}
                  >
                    <Plus className="size-4" /> {t('rulesets.addSize')}
                  </Button>
                </CardHeader>
                {sizes.length === 0 ? (
                  <CardContent>
                    <div style={{ fontSize: 13, color: 'var(--ad-text-muted)' }}>
                      {t('rulesets.addSizesHint')}
                    </div>
                  </CardContent>
                ) : (
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="ad-table" style={{ minWidth: 400 }}>
                        <thead>
                          <tr>
                            <th style={{ paddingLeft: 20 }}>{t('rulesets.width')}</th>
                            <th>{t('rulesets.height')}</th>
                            <th style={{ width: 52 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {sizes.map((entry, i) => (
                            <tr key={i}>
                              <td style={{ paddingLeft: 20 }}>
                                <NumberInput
                                  value={entry.width}
                                  onChange={(v) => updateSize(i, 'width', v ?? null)}
                                  min={0}
                                  placeholder={t('rulesets.width')}
                                />
                              </td>
                              <td>
                                <NumberInput
                                  value={entry.height}
                                  onChange={(v) => updateSize(i, 'height', v ?? null)}
                                  min={0}
                                  placeholder={t('rulesets.height')}
                                />
                              </td>
                              <td>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  title={t('common.remove')}
                                  onClick={() => removeSize(i)}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {selectedType === 'LAYOUT' && (
              <Card tone="creative">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Grid2x2} />
                    {t('rulesets.layouts')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('rulesets.layouts')}</label>
                      <form.Field name="layoutIds">
                        {(field) => (
                          <ChipMultiSelect
                            value={field.state.value}
                            onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                            options={layouts}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={t('rulesets.selectLayouts')}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team floor prices */}
            <Card tone="money">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Users} />
                  {t('rulesets.teamFloorPrices')}
                  {teamFPs.length > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--ad-text-muted)',
                        background: 'var(--ad-neutral-soft)',
                        padding: '1px 8px',
                        borderRadius: 999,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {teamFPs.length}
                    </span>
                  )}
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addTeamFloorPrice}
                >
                  <Plus className="size-4" /> {t('rulesets.addTeam')}
                </Button>
              </CardHeader>
              {teamFPs.length === 0 ? (
                <CardContent>
                  <div style={{ fontSize: 13, color: 'var(--ad-text-muted)' }}>
                    {t('rulesets.addTeamHint')}
                  </div>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="ad-table" style={{ minWidth: 500 }}>
                      <thead>
                        <tr>
                          <th style={{ paddingLeft: 20 }}>{t('common.team')}</th>
                          <th style={{ width: 160 }}>{t('rulesets.amount')}</th>
                          <th style={{ width: 100 }}>{t('rulesets.currency')}</th>
                          <th style={{ width: 52 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {teamFPs.map((entry, i) => (
                          <tr key={i}>
                            <td style={{ paddingLeft: 20 }}>
                              <Select value={entry.teamId || undefined} onValueChange={(v) => { if (v) handleTeamSelect(i, v) }} onOpenChange={(open) => { if (open) setAdvertiserTeamsEnabled(true) }}>
                                <SelectTrigger><SelectValue placeholder={t('rulesets.selectTeam')}>{teamDropdownOptions.find((o) => o.id === entry.teamId)?.name ?? (entry.teamName || null)}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {teamDropdownOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <NumberInput
                                value={entry.amount || null}
                                onChange={(v) => updateTeamFloorPrice(i, 'amount', v ?? 0)}
                                min={0}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <Select value={entry.currency} onValueChange={(v) => { if (v) updateTeamFloorPrice(i, 'currency', v) }}>
                                <SelectTrigger><SelectValue>{CURRENCY_OPTIONS.find((o) => o.value === entry.currency)?.label ?? null}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title={t('common.remove')}
                                onClick={() => removeTeamFloorPrice(i)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Advertiser floor prices */}
            <Card tone="money">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={IdCard} />
                  {t('rulesets.advertiserFloorPrices')}
                  {advFPs.length > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--ad-text-muted)',
                        background: 'var(--ad-neutral-soft)',
                        padding: '1px 8px',
                        borderRadius: 999,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {advFPs.length}
                    </span>
                  )}
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addAdvertiserFloorPrice}
                >
                  <Plus className="size-4" /> {t('rulesets.addAdvertiser')}
                </Button>
              </CardHeader>
              {advFPs.length === 0 ? (
                <CardContent>
                  <div style={{ fontSize: 13, color: 'var(--ad-text-muted)' }}>
                    {t('rulesets.addAdvertiserHint')}
                  </div>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="ad-table" style={{ minWidth: 500 }}>
                      <thead>
                        <tr>
                          <th style={{ paddingLeft: 20 }}>{t('rulesets.advertiser')}</th>
                          <th style={{ width: 160 }}>{t('rulesets.amount')}</th>
                          <th style={{ width: 100 }}>{t('rulesets.currency')}</th>
                          <th style={{ width: 52 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {advFPs.map((entry, i) => (
                          <tr key={i}>
                            <td style={{ paddingLeft: 20 }}>
                              <Select value={entry.advertiserId || undefined} onValueChange={(v) => { if (v) handleAdvertiserSelect(i, v) }}>
                                <SelectTrigger><SelectValue placeholder={t('rulesets.selectAdvertiser')}>{advertiserDropdownOptions.find((o) => o.id === entry.advertiserId)?.name ?? (entry.advertiserName || null)}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {advertiserDropdownOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <NumberInput
                                value={entry.amount || null}
                                onChange={(v) => updateAdvertiserFloorPrice(i, 'amount', v ?? 0)}
                                min={0}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <Select value={entry.currency} onValueChange={(v) => { if (v) updateAdvertiserFloorPrice(i, 'currency', v) }}>
                                <SelectTrigger><SelectValue>{CURRENCY_OPTIONS.find((o) => o.value === entry.currency)?.label ?? null}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title={t('common.remove')}
                                onClick={() => removeAdvertiserFloorPrice(i)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {!isNew && id && (
            <form.Field name="usage">
              {(field) => (
                <ResourceUsageCard
                  usageParam="siteRuleset"
                  resourceId={id}
                  edit={{
                    value: field.state.value,
                    onChange: field.handleChange,
                    help: t(
                      selectedType === 'INVENTORY'
                        ? 'resourceUsage.inventoryHelp'
                        : 'resourceUsage.listHelp'
                    ),
                  }}
                />
              )}
            </form.Field>
          )}
        </div>

        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => cancelNew('/publishing/rulesets')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('rulesets.createRuleset') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function RulesetEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const { data: ruleset, isLoading, isError } = useQuery({
    queryKey: ['ruleset', id],
    queryFn: () => RulesetsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  // The form starts from these, so it waits for them.
  // On failure the form falls back to no usage, which it then leaves untouched.
  const {
    data: usage,
    isPending: usagePending,
    isError: usageError,
  } = useQuery({
    queryKey: ['ruleset-usage', id],
    queryFn: () => RulesetsApi.getUsage(id!),
    enabled: !!ruleset && !isNew,
  })

  const crumbsLoading = [
    { label: t('nav.rulesets'), href: '/publishing/rulesets' },
    { label: isNew ? t('rulesets.newTitle') : t('rulesets.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if ((isLoading || (!!ruleset && usagePending && !usageError)) && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} aside />
      </AppShell>
    )
  }

  return (
    <RulesetEditForm
      key={id}
      ruleset={ruleset ?? null}
      usage={usage ?? NO_USAGE}
      id={id!}
      isNew={isNew}
    />
  )
}
